import {
  PDF_DETAIL_ROW_LIMIT,
  compactDateKey,
  downloadTextFile,
  formatRangeLabel,
  inManilaDateRange,
  MANILA_TIME_ZONE,
} from "./adminOrdersReport";
import { formatManilaDateTime } from "./dateTime";

export { PDF_DETAIL_ROW_LIMIT, downloadTextFile };

export const slugifyEventName = (value) => {
  const slug = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "event";
};

export const filterTicketsForExport = (
  tickets = [],
  { eventTitle = "", fromKey = "", toKey = "" } = {}
) => {
  const event = String(eventTitle || "").trim();
  if (!event) return [];

  return (tickets || []).filter((ticket) => {
    if (String(ticket?.eventTitle || "") !== event) return false;
    return inManilaDateRange(ticket?.purchasedAt, fromKey, toKey);
  });
};

export const summarizeTickets = (tickets = []) => {
  let usedCount = 0;
  let unusedCount = 0;
  const bySection = new Map();
  const byType = new Map();

  (tickets || []).forEach((ticket) => {
    const status = String(ticket?.status || "Unused");
    if (status === "Used") usedCount += 1;
    else unusedCount += 1;

    const section = String(ticket?.exactSection || "-").trim() || "-";
    const sectionRow = bySection.get(section) || { section, issued: 0, used: 0, unused: 0 };
    sectionRow.issued += 1;
    if (status === "Used") sectionRow.used += 1;
    else sectionRow.unused += 1;
    bySection.set(section, sectionRow);

    const type = String(ticket?.type || "General Admission").trim() || "General Admission";
    const typeRow = byType.get(type) || { type, issued: 0, used: 0, unused: 0 };
    typeRow.issued += 1;
    if (status === "Used") typeRow.used += 1;
    else typeRow.unused += 1;
    byType.set(type, typeRow);
  });

  return {
    issuedCount: tickets.length,
    usedCount,
    unusedCount,
    bySection: [...bySection.values()].sort((a, b) => b.issued - a.issued || a.section.localeCompare(b.section)),
    byType: [...byType.values()].sort((a, b) => b.issued - a.issued || a.type.localeCompare(b.type)),
  };
};

export const ticketsReportFilename = (eventTitle, fromKey = "", toKey = "", ext = "csv") => {
  const suffix = String(ext || "csv").replace(/^\./, "");
  const slug = slugifyEventName(eventTitle);
  if (!fromKey && !toKey) return `tickets-${slug}.${suffix}`;
  const start = compactDateKey(fromKey || toKey);
  const end = compactDateKey(toKey || fromKey);
  return `tickets-${slug}-${start}-${end}.${suffix}`;
};

const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const formatIssuedLabel = (fromKey, toKey) => {
  if (!fromKey && !toKey) return "All issued dates";
  return formatRangeLabel(fromKey, toKey);
};

export const buildTicketsCsv = ({
  tickets = [],
  summary,
  eventTitle = "",
  fromKey = "",
  toKey = "",
  generatedAt = new Date(),
} = {}) => {
  const totals = summary || summarizeTickets(tickets);
  const generated = generatedAt instanceof Date ? generatedAt.toISOString() : String(generatedAt || "");
  const sectionBreakdown = totals.bySection
    .map((row) => `${row.section}=${row.issued} (used ${row.used}, unused ${row.unused})`)
    .join("; ");

  const meta = [
    ["Report", "Echo Jham Tickets"],
    ["Generated at", generated],
    ["Event", eventTitle || ""],
    ["Issued date range", formatIssuedLabel(fromKey, toKey)],
    ["Issued tickets", totals.issuedCount],
    ["Used", totals.usedCount],
    ["Unused", totals.unusedCount],
    ["By section", sectionBreakdown || "none"],
  ];

  const headers = [
    "ticket_id",
    "order",
    "event",
    "attendee",
    "type",
    "section",
    "status",
    "scanned_at",
    "purchased_at",
    "row_seat",
  ];

  const rows = (tickets || []).map((ticket) => [
    ticket.ticketId || "",
    ticket.orderCode || "",
    ticket.eventTitle || "",
    ticket.attendee || "",
    ticket.type || "",
    ticket.exactSection || "-",
    ticket.status || "Unused",
    ticket.scannedAt ? formatManilaDateTime(ticket.scannedAt) : "Not scanned",
    ticket.purchasedAt ? formatManilaDateTime(ticket.purchasedAt) : "",
    ticket.seatLabel || "-",
  ]);

  return [
    ...meta.map((pair) => pair.map(csvEscape).join(",")),
    "",
    headers.join(","),
    ...rows.map((row) => row.map(csvEscape).join(",")),
  ].join("\n");
};

export const buildTicketsReportPdf = ({
  tickets = [],
  summary,
  eventTitle = "",
  fromKey = "",
  toKey = "",
  generatedAt = new Date(),
} = {}) => {
  const totals = summary || summarizeTickets(tickets);
  const includeDetailTable = tickets.length <= PDF_DETAIL_ROW_LIMIT;
  const { jsPDF } = require("jspdf");
  const doc = new jsPDF({
    unit: "pt",
    format: "a4",
    orientation: includeDetailTable && tickets.length > 0 ? "landscape" : "portrait",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  let y = 42;

  doc.setFillColor(139, 0, 0);
  doc.rect(0, 0, pageWidth, 56, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Echo Jham Tickets Report", margin, 34);

  y = 80;
  doc.setTextColor(40, 40, 40);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const generatedLabel = generatedAt instanceof Date
    ? generatedAt.toLocaleString("en-PH", { timeZone: MANILA_TIME_ZONE })
    : String(generatedAt);
  doc.text(`Event: ${eventTitle || "-"}`, margin, y);
  y += 16;
  doc.text(`Issued date range: ${formatIssuedLabel(fromKey, toKey)}`, margin, y);
  y += 16;
  doc.text(`Generated: ${generatedLabel}`, margin, y);
  y += 16;
  doc.text("This list is for one event. Date range filters when the ticket was issued, not show date.", margin, y);
  y += 28;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`Issued: ${totals.issuedCount}`, margin, y);
  doc.text(`Used: ${totals.usedCount}`, margin + 160, y);
  doc.text(`Unused: ${totals.unusedCount}`, margin + 300, y);
  y += 28;

  const ensureSpace = (needed) => {
    if (y + needed < pageHeight - margin) return;
    doc.addPage();
    y = margin;
  };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("By section", margin, y);
  y += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  if (totals.bySection.length === 0) {
    doc.text("No tickets in this export.", margin, y);
    y += 18;
  } else {
    totals.bySection.forEach((row) => {
      ensureSpace(14);
      doc.text(
        `${row.section}  ${row.issued} issued  ${row.used} used  ${row.unused} unused`,
        margin,
        y
      );
      y += 14;
    });
    y += 10;
  }

  if (includeDetailTable && tickets.length > 0) {
    ensureSpace(40);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Tickets", margin, y);
    y += 14;
    const columns = [
      { x: margin, width: 90 },
      { x: margin + 90, width: 70 },
      { x: margin + 160, width: 110 },
      { x: margin + 270, width: 90 },
      { x: margin + 360, width: 80 },
      { x: margin + 440, width: 50 },
      { x: margin + 490, width: 90 },
      { x: margin + 580, width: 70 },
    ];
    doc.setFontSize(8);
    doc.text("Ticket", columns[0].x, y);
    doc.text("Order", columns[1].x, y);
    doc.text("Attendee", columns[2].x, y);
    doc.text("Type", columns[3].x, y);
    doc.text("Section", columns[4].x, y);
    doc.text("Status", columns[5].x, y);
    doc.text("Issued", columns[6].x, y);
    doc.text("Seat", columns[7].x, y);
    y += 10;
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 12;
    doc.setFont("helvetica", "normal");

    tickets.forEach((ticket) => {
      ensureSpace(16);
      const cells = [
        String(ticket.ticketId || ""),
        String(ticket.orderCode || ""),
        String(ticket.attendee || ""),
        String(ticket.type || ""),
        String(ticket.exactSection || "-"),
        String(ticket.status || "Unused"),
        ticket.purchasedAt ? formatManilaDateTime(ticket.purchasedAt) : "-",
        String(ticket.seatLabel || "-"),
      ];
      cells.forEach((text, index) => {
        doc.text(doc.splitTextToSize(text, columns[index].width - 4)[0] || "", columns[index].x, y);
      });
      y += 14;
    });
  } else if (tickets.length > PDF_DETAIL_ROW_LIMIT) {
    ensureSpace(20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("This PDF is a summary. Download CSV for the full door list.", margin, y);
  }

  return doc;
};
